import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { ResponsiveSankey } from "@nivo/sankey";
import {
  Card,
  ElementContainer,
  FlexibleContainer,
  HorizontalLine,
  RowContainer,
} from "../../../components/containers/styledComponents";
import { graphPalette } from "../../../styles";
import { StewardshipFilterParam } from "../../../models/stewardshipModels";
import { usePost } from "../../../hooks/usePost";
import { CompassParagraph } from "../../../components/common/styledComponents";
import { SankeyControls } from "./SankeyControls";
import { LegendCircle } from "../../../components/visuals/styledComponents";
import { EngagementMilestone, SankeyNode } from "./models";
import { toSankeyData } from "./util";
import { nodeColors } from "./constants";
import { useToast } from "../../../store/ToastProvider";
import { Empty } from "antd";

const ChartWrap = styled.div`
  height: 320px;
  display: flex;
  flex: 1;
`;

const LegendContainer = styled.div`
  display: flex;
  max-height: 175px;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: auto;
  border-radius: 8px;
  margin-top: 12px;
`;

export const EngagementSankeyChart = ({
  filters,
}: {
  filters: StewardshipFilterParam;
}): JSX.Element => {
  const [includeCorporateIssues, setIncludeCorporateIssues] = useState(false);
  const {
    error,
    sendData: fetchEngagementCompaniesMateriality,
    isLoading,
    data: sourceData,
  } = usePost<EngagementMilestone[], StewardshipFilterParam>();

  const { addToast } = useToast();

  useEffect(() => {
    fetchEngagementCompaniesMateriality(
      "GET_ENGAGEMENT_RECORD_STATUSES",
      filters
    );
  }, [filters]);

  useEffect(() => {
    if (error) {
      addToast("Error fetching engagement progress data", "error");
    }
  }, [error]);

  const sankeyData = useMemo(
    () =>
      toSankeyData(sourceData || [], {
        excludeCorporateIssue: !includeCorporateIssues,
        nodeColors: nodeColors,
      }),
    [sourceData, includeCorporateIssues]
  );

  return (
    <Card h="100%" m="0 20px" isLoading={isLoading}>
      <RowContainer>
        <CompassParagraph fontSize={16} bold={true}>
          Engagement Progression
        </CompassParagraph>
      </RowContainer>
      <FlexibleContainer fd="row" ai="flex-start" jc="flex-start">
        <ElementContainer flexDirection="column">
          <SankeyControls
            isLoading={isLoading}
            includeCorporateIssues={includeCorporateIssues}
            setIncludeCorporateIssues={setIncludeCorporateIssues}
          />
          <HorizontalLine marginBottom={4} marginTop={1} />
          <LegendContainer>
            {sankeyData.links
              .sort((a, b) => a.target.localeCompare(b.target))
              .map((item) => (
                <ElementContainer key={item.target} mb={5}>
                  <LegendCircle
                    color={
                      sankeyData.nodes.find((node) => node.id === item.target)
                        ?.color as string
                    }
                  ></LegendCircle>
                  <CompassParagraph color="#8B909A" fontSize={12}>
                    {item.target}: {item.value}
                  </CompassParagraph>
                </ElementContainer>
              ))}
          </LegendContainer>
        </ElementContainer>
        {sankeyData.links.length && sankeyData.nodes.length ? (
          <ChartWrap>
            <ResponsiveSankey
              data={sankeyData}
              margin={{ top: 50, right: 240, bottom: 20, left: 140 }}
              align="justify"
              colors={(node: SankeyNode): string =>
                node.color || graphPalette[0]
              }
              nodeOpacity={1}
              nodeHoverOthersOpacity={0.35}
              nodeThickness={18}
              nodeSpacing={24}
              nodeBorderWidth={0}
              nodeBorderColor={{ from: "color" }}
              nodeBorderRadius={3}
              linkOpacity={0.5}
              linkHoverOthersOpacity={0.1}
              linkContract={0}
              enableLinkGradient={true}
              labelPosition="outside"
              labelPadding={16}
              labelTextColor={{ from: "color", modifiers: [["darker", 1]] }}
              theme={{
                labels: {
                  text: {
                    fontFamily: "Apercu, -apple-system, BlinkMacSystemFont",
                    fontSize: 13,
                    fontWeight: 500,
                  },
                },
                tooltip: {
                  container: {
                    fontFamily: "Apercu, -apple-system, BlinkMacSystemFont",
                  },
                },
              }}
            />
          </ChartWrap>
        ) : (
          <RowContainer jc="center" ai="center" flex="1">
            <ElementContainer>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={"No engagement progression records"}
              />
            </ElementContainer>
          </RowContainer>
        )}
      </FlexibleContainer>
    </Card>
  );
};
